import sdk from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { orderQueryKeys } from "./queryKeys";

/**
 * The trailing window the "Previous orders" quick action offers (KAN-51).
 * Fixed for this iteration rather than configurable - it is the default the
 * backend applies too, sent explicitly so the value the UI promises and the
 * value the server uses can't drift apart silently.
 */
export const PREVIOUS_ORDERS_WINDOW_DAYS = 90;

/**
 * Ceiling on how many previously-ordered products this feature will carry.
 *
 * The backend endpoint is deliberately unbounded (api#210), so the ceiling
 * belongs to the client that has to turn the list into one Typesense query.
 * 250 is Typesense's maximum `per_page`, and the results page asks for the
 * whole set in a single page so its recency ordering holds across it. A salon
 * with more than 250 distinct products in 90 days gets their 250 most recent,
 * which is the right truncation for a "what do I usually buy" shortcut.
 *
 * Applied inside {@link usePreviousOrderProductIds} rather than at each call
 * site: the count shown in the dropdown, the count reported to analytics and
 * the ids sent to Typesense have to be the same number, and the only way to
 * guarantee that is to have one list.
 */
export const PREVIOUS_ORDERS_MAX_PRODUCTS = 250;

/** Shared empty list, so "no ids" keeps a stable identity across renders. */
const NO_PRODUCT_IDS: string[] = [];

/**
 * The distinct products this customer ordered in the last
 * {@link PREVIOUS_ORDERS_WINDOW_DAYS} days, most-recently-ordered first.
 *
 * Served by `GET /store/order-history/previous-product-ids` (api#210), which
 * returns ids alone - the search index carries everything else.
 *
 * `retry: false` so a guest's 401 settles on the first attempt rather than
 * after react-query's default retries.
 */
export function previousProductIdsQuery(
  days: number = PREVIOUS_ORDERS_WINDOW_DAYS
) {
  return queryOptions({
    queryKey: orderQueryKeys.previousProductIds(days),
    queryFn: () => sdk.store.orderHistory.previousProductIds({ days }),
    retry: false,
    // A salon's 90-day purchase history does not change between two openings
    // of the search dropdown, so serve it from cache for the session's sake.
    staleTime: 5 * 60 * 1000
  });
}

/**
 * The capped id list plus the three states callers actually branch on.
 *
 * Returns ids rather than the raw response so no caller can reach the
 * uncapped list: an uncapped count in the dropdown would promise more products
 * than the results page can show.
 *
 * `isError` is surfaced separately from an empty list because the two mean
 * opposite things to a customer - "you have no orders in this window" versus
 * "we could not find out". The dropdown may collapse them (it omits the action
 * either way, which is the right fallback); the results page must not.
 *
 * @param options.enabled gate the fetch. The dropdown passes `isFocused` so
 * opening a page costs nothing and only focusing search pays for the request.
 */
export function usePreviousOrderProductIds(options?: { enabled?: boolean }) {
  const { data, isPending, isError, refetch } = useQuery({
    ...previousProductIdsQuery(),
    ...options
  });

  const productIds = useMemo(() => {
    const ids = data?.product_ids;
    if (!ids?.length) return NO_PRODUCT_IDS;
    return ids.slice(0, PREVIOUS_ORDERS_MAX_PRODUCTS);
  }, [data]);

  return { productIds, isPending, isError, refetch };
}
